import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'react-toastify';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import API_CONFIG from '../config/api.js';

// Suppress ReactQuill findDOMNode warning
const originalError = console.error;
console.error = (...args) => {
  if (typeof args[0] === 'string' && args[0].includes('findDOMNode')) {
    return;
  }
  originalError.call(console, ...args);
};

const PostForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEditMode = Boolean(id);

  const [formData, setFormData] = useState({
    title: '',
    subcategory: '',
    content: '',
    author: '',
    isPublished: true
  });

  const [categories, setCategories] = useState([]);
  const [subcategories, setSubcategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('');
  const [loading, setLoading] = useState(true);

  const fetchCategories = useCallback(async () => {
    try {
      const res = await axios.get(`${API_CONFIG.BASE_URL}/categories`);
      setCategories(res.data.data);
    } catch (err) {
      toast.error('Failed to fetch categories');
    }
  }, []);

  const fetchSubcategories = useCallback(async (categoryId) => {
    try {
      const res = await axios.get(`${API_CONFIG.BASE_URL}/subcategories/bycategory/${categoryId}`);
      setSubcategories(res.data.data);
    } catch (err) {
      toast.error('Failed to fetch subcategories');
    }
  }, []);

  const fetchPost = useCallback(async () => {
    try {
      const res = await axios.get(`${API_CONFIG.BASE_URL}/posts/${id}`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`
        }
      });
      const post = res.data.data;
      
      setFormData({
        title: post.title || '',
        subcategory: post.subcategory._id || '',
        content: post.content || '',
        author: post.author || '',
        isPublished: post.isPublished !== undefined ? post.isPublished : true
      });

      if (post.subcategory && post.subcategory.category) {
        setSelectedCategory(post.subcategory.category._id);
        await fetchSubcategories(post.subcategory.category._id);
      }
      
      setLoading(false);
    } catch (err) {
      console.error('Error fetching post:', err);
      toast.error(err.response?.data?.error || 'Failed to fetch post details');
      navigate('/posts');
    }
  }, [id, navigate, fetchSubcategories]);

  useEffect(() => {
    fetchCategories();
    if (isEditMode) {
      fetchPost();
    } else {
      setLoading(false);
    }
  }, [isEditMode, fetchCategories, fetchPost]);

  useEffect(() => {
    if (selectedCategory) {
      fetchSubcategories(selectedCategory);
    }
  }, [selectedCategory, fetchSubcategories]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value
    });
  };

  const handleCategoryChange = (e) => {
    const categoryId = e.target.value;
    setSelectedCategory(categoryId);
    setFormData({
      ...formData,
      subcategory: '' // Reset subcategory when category changes
    });
  };

  const handleEditorChange = (content) => {
    setFormData({
      ...formData,
      content
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.subcategory) {
      toast.error('Please select a subcategory');
      return;
    }
    if (!formData.author || !formData.author.trim()) {
      toast.error('Please enter an author');
      return;
    }
    
    try {
      const config = {
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('token')}`
        }
      };
      
      if (isEditMode) {
        await axios.put(
          `${API_CONFIG.BASE_URL}/posts/${id}`,
          formData,
          config
        );
        toast.success('Post updated successfully');
      } else {
        await axios.post(`${API_CONFIG.BASE_URL}/posts`, formData, config);
        toast.success('Post created successfully');
      }
      
      navigate('/posts');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to save post');
    }
  };

  // Configure Quill modules
  const quillModules = {
    toolbar: [
      [{ header: [1, 2, 3, 4, 5, 6, false] }],
      ['bold', 'italic', 'underline', 'strike'],
      [{ list: 'ordered' }, { list: 'bullet' }],
      [{ script: 'sub' }, { script: 'super' }],
      [{ indent: '-1' }, { indent: '+1' }],
      [{ direction: 'rtl' }],
      [{ color: [] }, { background: [] }],
      ['link'],
      ['clean']
    ]
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div>
      <h1 className="text-2xl font-semibold text-gray-900 mb-6">
        {isEditMode ? 'Edit Post' : 'Add Post'}
      </h1>

      <div className="bg-white shadow sm:rounded-lg p-6">
        <form onSubmit={handleSubmit}>
          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
            <div className="col-span-2 sm:col-span-1">
              <label htmlFor="title" className="form-label">
                Title (नेपाली) <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                id="title"
                name="title"
                value={formData.title}
                onChange={handleChange}
                required
                className="form-input"
              />
            </div>

            {/* English Title field removed as per requirement */}

            <div className="col-span-2 sm:col-span-1">
              <label htmlFor="category" className="form-label">
                Category <span className="text-red-500">*</span>
              </label>
              <select
                id="category"
                value={selectedCategory}
                onChange={handleCategoryChange}
                required
                className="form-input"
              >
                <option value="">Select Category</option>
                {categories.map((category) => (
                  <option key={category._id} value={category._id}>
                    {category.name}
                  </option>
                ))}
              </select>
            </div>

            <div className="col-span-2 sm:col-span-1">
              <label htmlFor="subcategory" className="form-label">
                Subcategory <span className="text-red-500">*</span>
              </label>
              <select
                id="subcategory"
                name="subcategory"
                value={formData.subcategory}
                onChange={handleChange}
                required
                disabled={!selectedCategory}
                className="form-input"
              >
                <option value="">Select Subcategory</option>
                {subcategories.map((subcategory) => (
                  <option key={subcategory._id} value={subcategory._id}>
                    {subcategory.name}
                  </option>
                ))}
              </select>
            </div>

            <div className="col-span-2 sm:col-span-1">
              <label htmlFor="author" className="form-label">
                Author <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                id="author"
                name="author"
                value={formData.author}
                onChange={handleChange}
                required
                className="form-input"
              />
            </div>

            {/* Tags field removed as per requirement */}

            {/* Excerpt field removed as per requirement */}

            <div className="col-span-2">
              <label htmlFor="content" className="form-label">
                Content <span className="text-red-500">*</span>
              </label>
              <div className="mt-1">
                <ReactQuill
                  theme="snow"
                  value={formData.content}
                  onChange={handleEditorChange}
                  modules={quillModules}
                  className="h-64 mb-12"
                />
              </div>
            </div>

            <div className="flex items-center">
              <input
                type="checkbox"
                id="isPublished"
                name="isPublished"
                checked={formData.isPublished}
                onChange={handleChange}
                className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
              />
              <label htmlFor="isPublished" className="ml-2 block text-sm text-gray-900">
                Published
              </label>
            </div>
          </div>

          <div className="mt-6 flex items-center justify-end space-x-3">
            <button
              type="button"
              onClick={() => navigate('/posts')}
              className="btn btn-secondary"
            >
              Cancel
            </button>
            <button type="submit" className="btn btn-primary">
              {isEditMode ? 'Update' : 'Create'} Post
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default PostForm; 